import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { taskAPI, crmUserAPI, CRMTask, CRMUser } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const CRMTasks: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<CRMTask[]>([]);
  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [showCompleted, setShowCompleted] = useState(false);
  const [assignedFilter, setAssignedFilter] = useState('');
  // 'unassigned' is not a user id, so it rides its own state rather than overloading the
  // assignee dropdown's value with a sentinel.
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [overdueFilter, setOverdueFilter] = useState(false);

  // Task Modal
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<CRMTask | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'MEDIUM',
    assigned_to: '',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadTasks();
  }, [showCompleted, assignedFilter, unassignedOnly, overdueFilter]);

  const loadUsers = async () => {
    try {
      const response = await crmUserAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (!showCompleted) params.completed = false;
      if (assignedFilter) params.assigned_to = assignedFilter;
      if (unassignedOnly) params.unassigned = true;
      if (overdueFilter) params.overdue = true;

      const response = await taskAPI.getAll(params);
      setTasks(response.data.results);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      await taskAPI.complete(taskId);
      loadTasks();
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const handleUncompleteTask = async (taskId: number) => {
    try {
      await taskAPI.uncomplete(taskId);
      loadTasks();
    } catch (err) {
      console.error('Error uncompleting task:', err);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    try {
      await taskAPI.delete(taskId);
      loadTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const openTaskModal = (task?: CRMTask) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title,
        description: task.description || '',
        due_date: task.due_date,
        priority: task.priority,
        assigned_to: task.assigned_to_id?.toString() || '',
      });
    } else {
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        due_date: '',
        priority: 'MEDIUM',
        assigned_to: '',
      });
    }
    setShowTaskModal(true);
  };

  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await taskAPI.update(editingTask.id, {
          title: taskForm.title,
          description: taskForm.description,
          due_date: taskForm.due_date,
          priority: taskForm.priority,
          assigned_to_id: taskForm.assigned_to ? parseInt(taskForm.assigned_to) : undefined,
        });
      } else {
        await taskAPI.create({
          title: taskForm.title,
          description: taskForm.description,
          due_date: taskForm.due_date,
          priority: taskForm.priority,
          assigned_to: taskForm.assigned_to ? parseInt(taskForm.assigned_to) : undefined,
        });
      }

      setShowTaskModal(false);
      loadTasks();
    } catch (err) {
      console.error('Error saving task:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPriorityClass = (priority: string) => {
    return `priority-badge ${priority.toLowerCase()}`;
  };

  // Group tasks by date
  const groupedTasks = tasks.reduce((groups: { [key: string]: CRMTask[] }, task) => {
    const date = task.due_date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(task);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedTasks).sort((a, b) =>
    new Date(a).getTime() - new Date(b).getTime()
  );

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <h1>Tasks</h1>
        <div className="crm-header-actions">
          <button className="btn btn-primary" onClick={() => openTaskModal()}>
            + Add Task
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Assigned To</label>
          <select
            className="filter-select"
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
          >
            <option value="">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.full_name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-filter">
          <label>
            <input
              type="checkbox"
              checked={unassignedOnly}
              onChange={(e) => setUnassignedOnly(e.target.checked)}
            />
            Unassigned only
          </label>
        </div>

        <div className="filter-group checkbox-filter">
          <label>
            <input
              type="checkbox"
              checked={overdueFilter}
              onChange={(e) => setOverdueFilter(e.target.checked)}
            />
            Overdue Only
          </label>
        </div>

        <div className="filter-group checkbox-filter">
          <label>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Show Completed
          </label>
        </div>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="loading">Loading tasks...</div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✓</div>
          <h3>No tasks found</h3>
          <p>
            {showCompleted
              ? 'No tasks match your filters.'
              : 'All caught up! No pending tasks.'}
          </p>
        </div>
      ) : (
        <div className="tasks-container">
          {sortedDates.map(date => (
            <div key={date} className="task-group">
              <div className={`task-group-header ${new Date(date) < new Date() && !showCompleted ? 'overdue' : ''}`}>
                {formatDate(date)}
                <span className="task-group-count">
                  {groupedTasks[date].length} task{groupedTasks[date].length !== 1 ? 's' : ''}
                </span>
              </div>
              <ul className="tasks-list">
                {groupedTasks[date].map(task => (
                  <li key={task.id} className={`task-row ${task.is_overdue ? 'overdue' : ''} ${task.completed ? 'completed' : ''}`}>
                    <div className="task-checkbox-wrapper">
                      <input
                        type="checkbox"
                        className="task-checkbox"
                        checked={task.completed}
                        onChange={() => task.completed ? handleUncompleteTask(task.id) : handleCompleteTask(task.id)}
                      />
                    </div>
                    <div className="task-main" onClick={() => openTaskModal(task)}>
                      <div className="task-title">{task.title}</div>
                      {task.description && (
                        <div className="task-description">{task.description}</div>
                      )}
                      <div className="task-links">
                        {task.lead_id && task.lead_name && (
                          <span
                            className="task-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/crm/leads/${task.lead_id}`);
                            }}
                          >
                            🏢 {task.lead_name}
                          </span>
                        )}
                        {task.customer_id && task.customer_name && (
                          <span className="task-link">
                            👤 {task.customer_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="task-meta-col">
                      <span className={getPriorityClass(task.priority)}>
                        {task.priority}
                      </span>
                    </div>
                    <div className="task-meta-col">
                      {task.assigned_to || (
                        <span style={{ color: '#b45309', fontWeight: 600 }}>Unassigned</span>
                      )}
                    </div>
                    <div className="task-actions">
                      <button
                        className="btn-icon"
                        onClick={() => openTaskModal(task)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteTask(task.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTask ? 'Edit Task' : 'Add Task'}</h2>
              <button className="modal-close" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveTask}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={taskForm.title}
                    onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                    placeholder="Task title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={taskForm.description}
                    onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                    placeholder="Task details..."
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={taskForm.due_date}
                      onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={taskForm.priority}
                      onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Assign To</label>
                  <select
                    value={taskForm.assigned_to}
                    onChange={e => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                  >
                    <option value="">Select user...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowTaskModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTask ? 'Update Task' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMTasks;
